import * as Apollo from '@apollo/client/react/components';
import { Grid, Hidden, Theme, Typography } from '@mui/material';
import * as Schema from 'generated/graphql/schema';
import { isEqual } from 'lodash';
import { WithRouterProps } from 'next/dist/client/with-router';
import Router, { withRouter } from 'next/router';
import React, { useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { compose } from 'recompose';
import { UrlObject, format } from 'url';

import { SearchValue } from '@/components/bars/search-bar';
import LoadingOrError from '@/components/loading/loading-or-error';
import Paginator from '@/components/paginator';
import * as Constants from '@/constants';
import { deviceMinimumData, devicesSimple } from '@/graphql/queries';
import { WithStyles, withStyles } from '@/hocs/with-styles';
import { WithWidth, withWidth } from '@/hocs/with-width';
import { updateDataHash } from '@/lib/hash';
import * as Types from '@/types';
import DeviceOverviewCard from '@/views/sensors/device-overview-card';
import DevicesTopBar from '@/views/sensors/overview-top-bar';
import DeviceOverviewVideoCard from '@/views/sensors/sensor-card-video';

type PeripheralType = NonNullable<Schema.DevicesSimpleQuery['devices'][0]>['peripherals'][0] & {
  parentDevice: NonNullable<Schema.DevicesSimpleQuery['devices'][0]>;
};

export type CameraType = {
  __typename?: 'Camera';
  streamURL?: string | null;
  _id?: string | null;
  index: string;
  peripheralId: string;
  name: string;
  description: string;
  peripheralType: PeripheralType;
  attachedSensors: Array<{
    __typename?: 'Sensor';
    _id?: string | null;
    peripheralId: string;
    peripheralType: PeripheralType;
    index: string;
    name: string;
    description: string;
  } | null>;
} & {
  parentDevice: Schema.DevicesSimpleQuery['devices'][0];
};

interface PeripheralCardsProperties {
  filteredPeripherals: PeripheralType[];
  currentPage: number;
  pageSize: number;
  timeAtLoad: Date;
}

// We extract the query, so that we can maintain a state for each Apollo query.
const InternalDeviceCard = ({
  peripheral,
  deviceVariables,
}: {
  peripheral: PeripheralType;
  deviceVariables: Schema.DeviceMinimumDataQueryVariables;
}) => {
  const [dataHash, setDataHash] = useState('');

  return (
    <Apollo.Query<Schema.DeviceMinimumDataQuery, Schema.DeviceMinimumDataQueryVariables>
      ssr={false}
      partialRefetch
      context={{ batchFour: true }}
      errorPolicy="all" // Necessary for returning partial data.
      key={`${peripheral.parentDevice.uuid}${peripheral.index}`}
      query={deviceMinimumData}
      fetchPolicy={'cache-and-network'}
      onCompleted={updateDataHash(setDataHash)}
      variables={deviceVariables}
    >
      {({ data, loading }) => {
        const sensor = (data?.device?.peripheral ?? (peripheral as any)) as Schema.Sensor;

        return (
          <DeviceOverviewCard
            key={`${peripheral.parentDevice.uuid}${sensor.index}`}
            device={peripheral.parentDevice}
            dataHash={dataHash}
            sensor={sensor}
            loading={loading}
            refetchQueries={[
              {
                query: deviceMinimumData,
                variables: deviceVariables,
              },
            ]}
          />
        );
      }}
    </Apollo.Query>
  );
};

const PeripheralCards: React.FC<PeripheralCardsProperties> = ({
  filteredPeripherals,
  currentPage,
  pageSize,
  timeAtLoad,
}) => {
  const mostRecentMinute = Math.round(timeAtLoad.getTime() / (1000 * 60)) * 1000 * 60;

  const currentPagePeripherals = [...filteredPeripherals].slice(
    currentPage * pageSize,
    currentPage * pageSize + pageSize,
  );

  /* Note:
   *  The below piece of code deliberately launches a request per sensor card in view.
   *  This is done in order to better utilize the client side cache from already loaded devices,
   *  while filtering and changing page. Another pro of doing it as potentially 6 requests,
   *  is that the loading of the cards are asynchronous, which gives a great user experience.
   *  The alternative was to make a single request for all 6 devices, loosing the above pros.
   */
  return (
    <>
      {currentPagePeripherals.map((peripheral) => {
        if (peripheral.peripheralType === Schema.PeripheralType.SENSOR) {
          const deviceVariables: Schema.DeviceMinimumDataQueryVariables = {
            uuid: peripheral.parentDevice.uuid,
            index: peripheral.index,
            time: [
              {
                from: new Date(mostRecentMinute - (8).hours).toISOString(),
                to: new Date(mostRecentMinute).toISOString(),
              },
            ],
            points: Constants.SAMPLE_POINTS,
          };

          return (
            <InternalDeviceCard
              key={peripheral._id || Math.random()}
              peripheral={peripheral}
              deviceVariables={deviceVariables}
            />
          );
        } else {
          return (
            <DeviceOverviewVideoCard
              key={`${peripheral.parentDevice.uuid}${peripheral.index}`}
              device={peripheral.parentDevice}
              camera={peripheral as unknown as CameraType}
            />
          );
        }
      })}
    </>
  );
};

const styles = (theme: Theme) => ({
  fillHeight: {
    height: '100%',
  },
  flexContainer: {
    display: 'flex',
  },
});

interface Properties {
  // Align all data times, by getting the date from the parent component.
  timeAtLoad: Date;
}

type SuperProps = Properties & WithTranslation & WithRouterProps & WithStyles<typeof styles> & WithWidth;

interface State {
  search: SearchValue[];
  page: number;
  compareDevices: Schema.DevicesSimpleQuery['devices'];
  flattenedPeripherals: Array<
    NonNullable<Schema.DevicesSimpleQuery['devices'][0]>['peripherals'][0] & {
      parentDevice: Schema.DevicesSimpleQuery['devices'][0];
    }
  >;
}

class DeviceCardPage extends React.Component<SuperProps, State> {
  readonly state: State = {
    search: [],
    page: 0,
    compareDevices: [],
    flattenedPeripherals: [],
  };

  private pageSize = Constants.PAGE_SIZE;

  constructor(props: SuperProps) {
    super(props);
    if (props.router.query.search) {
      try {
        const searchQuery = Array.isArray(props.router.query.search)
          ? props.router.query.search[0]
          : props.router.query.search;
        this.state.search = JSON.parse(searchQuery);
      } catch (routerError) {
        console.error(routerError);
      }
    }
    if (props.router.query.page) {
      try {
        const pageQuery = Array.isArray(props.router.query.page) ? props.router.query.page[0] : props.router.query.page;
        this.state.page = parseInt(pageQuery, 10);
      } catch (routerError) {
        console.error(routerError);
      }
    }
    this.updateSessionStorage({
      pathname: props?.router?.pathname ?? '/sensors',
      query: props?.router?.query ?? {},
    });
  }

  public render() {
    const { width, timeAtLoad } = this.props;

    return (
      <Apollo.Query<Schema.DevicesSimpleQuery, Schema.DevicesSimpleQueryVariables>
        query={devicesSimple}
        partialRefetch
        onCompleted={this.finishedQueringDevice}
        ssr={false}
        fetchPolicy="cache-and-network"
        errorPolicy="all" // Necessary for returning partial data.
      >
        {({ loading, error, data }) => {
          const devices = data?.devices ?? [];
          const wrappedMessage = !loading && !error && !devices.length ? this.getWrappedMessage() : undefined;
          // We only short-circuit the render if the query is loading or returned only an error (i.e. not an error + data).
          if ((loading && !data) || (error && !data)) {
            return (
              <>
                <DevicesTopBar
                  search={this.state.search}
                  page={this.state.page}
                  pageSize={this.pageSize}
                  filteredPeripherals={[]}
                  handlePageChange={this.handlePageChange}
                  handleSearchChange={this.handleSearchChange}
                />
                <LoadingOrError loading={loading} error={error} wrappedMessage={wrappedMessage} />
              </>
            );
          }
          const filteredPeripherals = this.filterPeripherals(devices.filter(Types.isTruthy));
          return (
            <>
              <DevicesTopBar
                search={this.state.search}
                page={this.state.page}
                pageSize={this.pageSize}
                filteredPeripherals={filteredPeripherals}
                handlePageChange={this.handlePageChange}
                handleSearchChange={this.handleSearchChange}
              />

              <LoadingOrError loading={loading} error={error} data={data} wrappedMessage={wrappedMessage}>
                <Grid container spacing={1} alignItems="stretch" justifyContent="space-between">
                  <PeripheralCards
                    filteredPeripherals={filteredPeripherals}
                    currentPage={this.state.page}
                    pageSize={this.pageSize}
                    timeAtLoad={timeAtLoad}
                  />
                  <Hidden mdUp>
                    <React.Suspense fallback={<div />}>
                      <Paginator
                        totalPages={Math.ceil(filteredPeripherals.length / this.pageSize)}
                        currentPage={this.state.page}
                        onCurrentPageChange={this.handlePageChange}
                        totalCount={filteredPeripherals.length}
                        pageSize={this.pageSize}
                      />
                    </React.Suspense>
                  </Hidden>
                </Grid>
              </LoadingOrError>
            </>
          );
        }}
      </Apollo.Query>
    );
  }
  private filterPeripherals = (devices: Schema.DevicesSimpleQuery['devices']) => {
    let { flattenedPeripherals } = this.state;

    // This covers hot reloading, which won't call onCompleted again, and clears state.
    if (!flattenedPeripherals || flattenedPeripherals.length === 0) {
      flattenedPeripherals = devices.reduce<typeof flattenedPeripherals>((prev, cur) => {
        prev.push(
          ...(cur?.peripherals || []).filter(Types.isTruthy).map((peripheral) => ({
            ...peripheral,
            parentDevice: cur,
          })),
        );

        return prev;
      }, [] as typeof flattenedPeripherals);
    }
    const filteredPeripherals = flattenedPeripherals.filter((peripheral) => {
      // If we haven't started searching yet, include all results.
      if (!this.state.search.length) {
        return true;
      }
      // Filter out both results and remaining suggestions, based on the search filter.
      let foundInSearch = false;
      this.state.search.map((searchTerm) => {
        if (searchTerm && searchTerm.value && searchTerm.value.value) {
          foundInSearch =
            peripheral.name.toLowerCase().includes(searchTerm.value.value.toLowerCase()) ||
            peripheral.description.toLowerCase().includes(searchTerm.value.value.toLowerCase()) ||
            peripheral.peripheralId?.toLowerCase().includes(searchTerm.value.value.toLowerCase());
        }
      });

      return foundInSearch;
    });

    // Sort the devices alphabetically, to always give a consistent view to the user.
    return filteredPeripherals
      .sort((left, right) => left.name.localeCompare(right.name))
      .sort((peripheral) => {
        return +(peripheral.peripheralType === Schema.PeripheralType.CAMERA);
      }) as PeripheralType[];
  };

  private getWrappedMessage = () => (
    <>
      <Typography variant="body2">
        {this.props.t(['shared:accessToNoDevices'], {
          defaultValue: 'You do not have access to any sensors yet!',
        })}
      </Typography>
      <Typography variant="body2">
        {this.props.t(['shared:contactLocalAdministrator'], {
          defaultValue: 'Please contact your local administrator for help.',
        })}
      </Typography>
    </>
  );

  private finishedQueringDevice = (data: Schema.DevicesSimpleQuery | null) => {
    const devices = (data?.devices ?? []).filter(Types.isTruthy);
    const { flattenedPeripherals } = this.state;
    // Flatten the devices only when we get new data.
    if (devices.length !== 0 && (!flattenedPeripherals?.length || !isEqual(devices, this.state.compareDevices))) {
      const updatedFlattenedPeripherals = devices
        .filter((device) => device)
        .reduce((prev, cur) => {
          prev.push(
            ...(cur.peripherals || []).filter(Types.isTruthy).map((peripheral) => ({
              ...peripheral,
              parentDevice: cur,
            })),
          );
          return prev;
        }, [] as typeof flattenedPeripherals);
      this.setState({
        flattenedPeripherals: updatedFlattenedPeripherals,
        compareDevices: devices,
      });
    }
  };

  private updateSessionStorage = (newRoute: UrlObject) => {
    // Update the session (tab specific) storage entry for navigation.
    if (typeof Storage !== 'undefined') {
      window.sessionStorage.setItem(
        Constants.SESSION_STORAGE_DEVICES_NAVIGATION_OVERVIEW_SEARCH_STATE_KEY,
        JSON.stringify({
          route: {
            pathname: newRoute.pathname,
            query: newRoute.query,
          },
        }),
      );
    }
  };

  private handlePageChange = async (page: number) => {
    const { pathname, query } = this.props.router;
    this.setState({ page });

    const updatedQuery = { ...query, page };
    const url = format({ pathname, query: updatedQuery });
    this.updateSessionStorage({ pathname, query: updatedQuery });

    await Router.replace(url, url, { shallow: true });
  };

  private handleSearchChange = async (val: SearchValue[]) => {
    const { pathname, query } = this.props.router;
    this.setState({ search: val, page: 0 });

    const updatedQuery = { ...query, search: JSON.stringify(val), page: 0 };
    const url = format({ pathname, query: updatedQuery });
    this.updateSessionStorage({ pathname, query: updatedQuery });

    await Router.replace(url, url, { shallow: true });
  };
}

const enhance = compose<SuperProps, Properties>(
  withTranslation(['shared', 'device']),
  withRouter,
  withStyles(styles),
  withWidth(),
);

export default enhance(DeviceCardPage);
